import React from 'react';
import { Download } from 'lucide-react';
import { useAdmin } from '../context/AdminContext';
import { exportToCSV } from '../utils/adminHelpers';

export default function RevenuePage() {
  const {
    loadingRevenue, revenueStats, revenueByPlan, revenueSummary,
    revenueGroupBy, setRevenueGroupBy, revenueFilterPreset,
    handleRevenuePresetFilter, handleRevenueFilterApply, handleRevenueReset,
    revenueLocalStartDate, setRevenueLocalStartDate,
    revenueLocalEndDate, setRevenueLocalEndDate,
  } = useAdmin();

  if (loadingRevenue) {
    return (
      <div className="dashboard-content">
        <h1 className="page-title">Revenu d'abonnements</h1>
        <div className="loading-spinner"><div className="spinner"></div><p>Chargement des données de revenu...</p></div>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <h1 className="page-title" style={{ margin: 0 }}>Revenu d'abonnements</h1>
          <button className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            onClick={() => {
              const rows = revenueStats.length > 0
                ? revenueStats.map(s => ({ Période: s.date || s.period || s._id || '—', Montant: s.totalRevenue || s.revenue || 0, Transactions: s.count || s.transactions || 0 }))
                : revenueByPlan.map(p => ({ Plan: p.plan_type || p._id || '—', Montant: p.totalRevenue || 0, Abonnements: p.count || 0 }));
              exportToCSV('revenus', rows);
            }}>
            <Download size={16} /> Exporter CSV
          </button>
        </div>

        {/* Filtres Prédéfinis */}
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
          {[
            { key: '30days', label: '📅 30 derniers jours', color: '#FF7800' },
            { key: '1year', label: '📆 1 an', color: '#FF7800' },
            { key: 'alltime', label: '🕐 All Time', color: '#10b981' },
          ].map(({ key, label, color }) => (
            <button key={key} onClick={() => handleRevenuePresetFilter(key)}
              style={{ padding: '0.5rem 1.5rem', borderRadius: '8px', border: 'none', background: revenueFilterPreset === key ? color : '#f0f4f8', color: revenueFilterPreset === key ? 'white' : '#414852', fontWeight: revenueFilterPreset === key ? '700' : '600', cursor: 'pointer', fontSize: '0.875rem' }}>
              {label}
            </button>
          ))}
        </div>

        {/* Filtres personnalisés */}
        <div className="page-actions" style={{ gap: '1rem', display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.75rem', color: '#5C636E', fontWeight: '600', textTransform: 'uppercase' }}>Personnalisé:</span>
          <input type="date" value={revenueLocalStartDate} onChange={(e) => setRevenueLocalStartDate(e.target.value)} style={{ padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #E9EBEF', fontSize: '0.875rem' }} />
          <input type="date" value={revenueLocalEndDate} onChange={(e) => setRevenueLocalEndDate(e.target.value)} style={{ padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #E9EBEF', fontSize: '0.875rem' }} />
          <select value={revenueGroupBy} onChange={(e) => setRevenueGroupBy(e.target.value)} style={{ padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #E9EBEF', fontSize: '0.875rem', background: 'white' }}>
            <option value="day">Par jour</option>
            <option value="month">Par mois</option>
          </select>
          <button className="btn-primary" onClick={handleRevenueFilterApply} style={{ padding: '0.5rem 1.5rem' }}>Appliquer</button>
          <button className="btn-secondary" onClick={handleRevenueReset} style={{ padding: '0.5rem 1.5rem' }}>Réinitialiser</button>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
        {[
          { label: 'Revenu Total', value: `${revenueSummary.totalRevenue.toLocaleString()} FC`, color: '#10b981', border: '#10b981' },
          { label: 'Abonnements Actifs', value: revenueSummary.activeSubscriptions, color: '#FF7800', border: '#FF7800' },
          { label: 'Abonnements Expirés', value: revenueSummary.expiredSubscriptions, color: '#FFB900', border: '#FFB900' },
          { label: 'Revenu Moyen', value: `${revenueSummary.averagePrice.toLocaleString()} FC`, color: '#FF7800', border: '#FF7800' },
        ].map(({ label, value, color, border }) => (
          <div key={label} style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderLeft: `4px solid ${border}` }}>
            <p style={{ fontSize: '0.875rem', color: '#5C636E', marginBottom: '0.5rem', textTransform: 'uppercase', fontWeight: '600' }}>{label}</p>
            <p style={{ fontSize: '2rem', fontWeight: '700', color, margin: '0.5rem 0' }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Revenus par Plan */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', marginBottom: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <h3 style={{ marginBottom: '1.5rem', color: '#1E2229', fontSize: '1.125rem', fontWeight: '600' }}>Revenus par Plan</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#FFFFFF', borderBottom: '2px solid #E9EBEF' }}>
                {['Plan', 'Abonnements', 'Actifs', 'Revenu Total', '% du Total'].map(h => (
                  <th key={h} style={{ padding: '1rem', textAlign: h === 'Plan' ? 'left' : 'right', fontWeight: '700', color: '#414852', fontSize: '0.875rem', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {revenueByPlan.map((plan, idx) => {
                const percentage = revenueSummary.totalRevenue > 0 ? ((plan.totalRevenue / revenueSummary.totalRevenue) * 100).toFixed(1) : 0;
                return (
                  <tr key={idx} style={{ borderBottom: '1px solid #E9EBEF', background: idx % 2 === 0 ? '#ffffff' : '#FFFFFF' }}>
                    <td style={{ padding: '1rem', color: '#1E2229', fontWeight: '600' }}>{plan.planName}</td>
                    <td style={{ padding: '1rem', textAlign: 'right', color: '#5C636E' }}>{plan.count}</td>
                    <td style={{ padding: '1rem', textAlign: 'right', color: '#10b981', fontWeight: '600' }}>{plan.activeCount}</td>
                    <td style={{ padding: '1rem', textAlign: 'right', color: '#1E2229', fontWeight: '700' }}>{plan.totalRevenue.toLocaleString()} FC</td>
                    <td style={{ padding: '1rem', textAlign: 'right', color: '#FF7800', fontWeight: '600' }}>{percentage}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bar chart */}
      {revenueByPlan.length > 0 && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', marginBottom: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h3 style={{ marginBottom: '1.5rem', color: '#1E2229', fontSize: '1.125rem', fontWeight: '600' }}>Visualisation Revenus par Plan</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {revenueByPlan.map((plan, idx) => {
              const maxRevenue = Math.max(...revenueByPlan.map(p => p.totalRevenue));
              const pct = (plan.totalRevenue / maxRevenue) * 100;
              const colors = ['#10b981', '#FF7800', '#FFB900', '#FF7800', '#06b6d4', '#dc2626'];
              const color = colors[idx % colors.length];
              return (
                <div key={idx}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: '600', color: '#1E2229' }}>{plan.planName}</span>
                    <span style={{ color: '#5C636E', fontSize: '0.875rem' }}>{plan.totalRevenue.toLocaleString()} FC</span>
                  </div>
                  <div style={{ background: '#E9EBEF', borderRadius: '8px', height: '24px', overflow: 'hidden' }}>
                    <div style={{ background: color, height: '100%', width: `${pct}%`, transition: 'width 0.3s ease' }}></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stats par date */}
      {revenueStats.length > 0 && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h3 style={{ marginBottom: '1.5rem', color: '#1E2229', fontSize: '1.125rem', fontWeight: '600' }}>Statistiques {revenueGroupBy === 'month' ? 'Mensuelles' : 'Quotidiennes'}</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#FFFFFF', borderBottom: '2px solid #E9EBEF' }}>
                  {['Période', 'Revenu', 'Abonnements', 'Actifs'].map(h => (
                    <th key={h} style={{ padding: '1rem', textAlign: h === 'Période' ? 'left' : 'right', fontWeight: '700', color: '#414852', fontSize: '0.875rem', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {revenueStats.slice().reverse().map((stat, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #E9EBEF', background: idx % 2 === 0 ? '#ffffff' : '#FFFFFF' }}>
                    <td style={{ padding: '1rem', color: '#1E2229', fontWeight: '600' }}>
                      {revenueGroupBy === 'month' ? new Date(`${stat.date}-01`).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' }) : new Date(stat.date).toLocaleDateString('fr-FR')}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right', color: '#10b981', fontWeight: '700' }}>{stat.revenue.toLocaleString()} FC</td>
                    <td style={{ padding: '1rem', textAlign: 'right', color: '#FF7800' }}>{stat.count}</td>
                    <td style={{ padding: '1rem', textAlign: 'right', color: '#FFB900', fontWeight: '600' }}>{stat.activeCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
